/**
 * UserManual - Componente de Manual do Usuário
 * Exibe guias e tutoriais em um Sheet lateral
 */
import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { 
  BookOpen, 
  HelpCircle, 
  CheckCircle2, 
  ArrowRight,
  Lightbulb,
  AlertCircle,
  Star
} from 'lucide-react';

// ============================================
// MANUAL DO RECRUTADOR
// ============================================
export const recruiterManualSections = [
  {
    id: 'intro',
    title: '👋 Bem-vindo ao G2eGente',
    content: `O G2eGente é uma plataforma de recrutamento ético que conecta sua empresa aos melhores talentos, 
    com foco em inclusão, consentimento e conformidade com a LGPD.`,
    tips: [
      'Todas as ações dos candidatos são baseadas em consentimento explícito',
      'A plataforma protege dados sensíveis automaticamente',
      'Candidatos podem revogar consentimento a qualquer momento'
    ]
  },
  {
    id: 'engenharia-cargo',
    title: '📋 Engenharia de Cargo',
    content: `A Engenharia de Cargo é o primeiro passo para um recrutamento eficiente. 
    Aqui você define o perfil ideal do candidato antes de publicar a vaga.`,
    steps: [
      'Acesse "Engenharia de Cargo" no menu lateral',
      'Clique em "Novo Cargo" para criar um perfil',
      'Defina o título, descrição e requisitos do cargo',
      'Configure o perfil DISC desejado respondendo ao questionário',
      'Defina os níveis de competência esperados',
      'Salve o cargo para usar em futuras vagas'
    ],
    tips: [
      'Um cargo bem definido aumenta a qualidade dos candidatos',
      'O perfil DISC ajuda a encontrar candidatos com comportamento adequado',
      'Você pode reutilizar cargos em múltiplas vagas'
    ]
  },
  {
    id: 'criar-vaga',
    title: '💼 Criando uma Nova Vaga',
    content: `Publique vagas de forma rápida e eficiente, com todas as informações 
    que os candidatos precisam para se candidatar.`,
    steps: [
      'Acesse "Minhas Vagas" no menu lateral',
      'Clique em "+ Nova Vaga"',
      'Selecione um Cargo (Engenharia de Cargo) ou preencha manualmente',
      'Preencha: Título, Descrição, Localização, Tipo de Contrato',
      'Adicione: Salário, Horário de Trabalho e Benefícios',
      'Configure o Perfil Ideal na aba "Perfil Ideal do Candidato"',
      'Use as Observações Internas para anotações privadas',
      'Clique em "Publicar Vaga" para disponibilizar aos candidatos'
    ],
    tips: [
      'Vagas com salário informado recebem mais candidaturas',
      'Descreva claramente os benefícios oferecidos',
      'Use o campo de observações para anotar detalhes importantes'
    ]
  },
  {
    id: 'selecionar-candidatos',
    title: '🔍 Selecionando Candidatos',
    content: `Encontre os candidatos ideais usando filtros avançados e o ranking automático 
    baseado no perfil ideal configurado.`,
    steps: [
      'Acesse "Selecionar Candidatos" no menu lateral',
      'Use os filtros para refinar a busca (cargo, localização, DISC, etc.)',
      'Ative "Trabalho Eventual" para buscar freelancers',
      'Visualize o ranking automático baseado no perfil ideal',
      'Clique em um candidato para ver detalhes completos',
      'Use "Fixar Candidato" para adicionar à vaga',
      'Acompanhe o status de cada candidato na vaga'
    ],
    tips: [
      'O ranking considera: DISC, experiência e competências',
      'Candidatos com "Trava por CNPJ" não aparecem para sua empresa',
      'Use a busca por nome para encontrar candidatos específicos'
    ]
  },
  {
    id: 'gestao-empresas',
    title: '🏢 Gestão Multi-Empresa',
    content: `Se você é um recrutador que atende múltiplas empresas, pode gerenciar 
    todas elas em uma única conta.`,
    steps: [
      'Acesse "Configurações" no menu lateral',
      'Role até "Empresas Gerenciadas"',
      'Clique em "Adicionar Empresa"',
      'Digite o CNPJ da empresa cliente',
      'Os dados serão buscados automaticamente na Receita Federal',
      'Use o seletor no topo do dashboard para alternar entre empresas'
    ],
    tips: [
      'Cada empresa tem seus próprios processos seletivos',
      'O seletor no header permite troca rápida de contexto',
      'Vagas e candidatos são separados por empresa'
    ]
  },
  {
    id: 'relatorios',
    title: '📊 Relatórios',
    content: `Acompanhe métricas importantes do seu processo de recrutamento 
    e exporte relatórios em PDF.`,
    steps: [
      'Acesse "Relatórios" no menu lateral',
      'Escolha o tipo: Visão Geral, Recrutamento, Funcionários ou Desempenho',
      'Visualize as métricas e gráficos',
      'Clique em "Exportar PDF" para baixar o relatório'
    ],
    tips: [
      'Use relatórios para apresentar resultados à diretoria',
      'O Relatório ESG mostra métricas de inclusão e diversidade',
      'Exporte regularmente para manter histórico'
    ]
  },
  {
    id: 'disc',
    title: '🧠 Perfil DISC',
    content: `O DISC é uma metodologia de análise comportamental que identifica 
    4 tipos de perfil: Dominância, Influência, Estabilidade e Conformidade.`,
    steps: [
      'D (Dominância): Foco em resultados, competitivo, direto',
      'I (Influência): Comunicativo, entusiasmado, persuasivo',
      'S (Estabilidade): Paciente, confiável, trabalho em equipe',
      'C (Conformidade): Analítico, preciso, focado em qualidade'
    ],
    tips: [
      'Não existe perfil "melhor" - depende do cargo',
      'Cargos de liderança geralmente requerem mais D e I',
      'Cargos técnicos geralmente requerem mais C e S',
      'O questionário tem 25 perguntas e leva ~10 minutos'
    ]
  },
  {
    id: 'lgpd',
    title: '🔒 LGPD e Consentimento',
    content: `A plataforma foi desenvolvida em conformidade com a Lei Geral de 
    Proteção de Dados (LGPD).`,
    steps: [
      'Candidatos devem consentir antes de ter dados visíveis',
      'O consentimento é registrado com data e hora',
      'Candidatos podem revogar consentimento a qualquer momento',
      'Dados são anonimizados quando o consentimento é revogado',
      'Relatórios de consentimento estão disponíveis para auditoria'
    ],
    tips: [
      'Sempre respeite a decisão do candidato sobre seus dados',
      'Não compartilhe dados de candidatos fora da plataforma',
      'A "Trava por CNPJ" protege candidatos empregados'
    ]
  }
];

// ============================================
// MANUAL DE AVALIAÇÃO DE DESEMPENHO
// ============================================
export const performanceManualSections = [
  {
    id: 'intro',
    title: '👋 Introdução à Avaliação de Desempenho',
    content: `O módulo de Avaliação de Desempenho permite avaliar seus funcionários 
    de forma estruturada, baseada em competências e comportamentos.`,
    tips: [
      'Avaliações regulares melhoram a retenção de talentos',
      'Feedback construtivo aumenta o engajamento',
      'Use dados objetivos para embasar suas avaliações'
    ]
  },
  {
    id: 'ciclos',
    title: '📅 Ciclos de Avaliação',
    content: `Os ciclos de avaliação são períodos definidos para realizar as avaliações. 
    Podem ser anuais, semestrais ou conforme sua necessidade.`,
    steps: [
      'Acesse "Avaliação de Desempenho" no menu lateral',
      'Clique em "Novo Ciclo" para criar um ciclo',
      'Defina: Nome, Data Início, Data Fim e Tipo',
      'Selecione os funcionários que participarão',
      'Configure os pesos de cada nível de avaliação',
      'Salve e inicie o ciclo quando estiver pronto'
    ],
    tips: [
      'Planeje ciclos com antecedência',
      'Comunique aos funcionários sobre o período de avaliação',
      'Ciclos podem ter status: Rascunho, Ativo ou Encerrado'
    ]
  },
  {
    id: 'niveis',
    title: '📊 Níveis de Avaliação',
    content: `As avaliações são estruturadas em 4 níveis que cobrem diferentes 
    aspectos do desempenho do funcionário.`,
    steps: [
      '1. Nível de Entrega (Competências Técnicas): Domínio de ferramentas, certificações, resultados mensuráveis',
      '2. Nível de Comportamento (Soft Skills): Comunicação, trabalho em equipe, resiliência',
      '3. Nível de Realização (Potencial): Iniciativa, inovação, visão estratégica',
      '4. Nível de Valores (Fit Cultural): Alinhamento com valores, ética, comprometimento'
    ],
    tips: [
      'Cada nível tem peso configurável (padrão: 25% cada)',
      'Ajuste os pesos conforme a cultura da empresa',
      'Use exemplos concretos ao avaliar cada aspecto'
    ]
  },
  {
    id: 'pontuacao',
    title: '⭐ Sistema de Pontuação',
    content: `A pontuação varia de 0 a 5 para cada item avaliado. 
    A nota final determina a classificação do funcionário.`,
    steps: [
      '0 - Não demonstra a competência',
      '1 - Demonstra raramente',
      '2 - Demonstra ocasionalmente',
      '3 - Demonstra frequentemente (esperado)',
      '4 - Demonstra consistentemente (acima do esperado)',
      '5 - Referência/Excelência (excepcional)'
    ],
    tips: [
      'Nota 3 é o "esperado" - reserve 4 e 5 para casos reais de destaque',
      'Documente exemplos para justificar notas altas ou baixas',
      'Seja consistente na pontuação entre funcionários'
    ]
  },
  {
    id: 'classificacao',
    title: '🏆 Classificação Final',
    content: `Com base na média ponderada dos níveis, o funcionário recebe uma 
    classificação que indica seu desempenho geral.`,
    steps: [
      '⭐ Excepcional (4.5 - 5.0): Desempenho extraordinário, referência para a equipe',
      '✅ Supera Expectativas (3.5 - 4.4): Desempenho acima do esperado',
      '📊 Atende Expectativas (2.5 - 3.4): Desempenho dentro do esperado',
      '⚠️ Abaixo das Expectativas (0 - 2.4): Necessita desenvolvimento'
    ],
    tips: [
      'Funcionários "Excepcionais" devem ser reconhecidos e retidos',
      'Funcionários "Abaixo" precisam de PDI (Plano de Desenvolvimento)',
      'A maioria deve estar em "Atende" ou "Supera"'
    ]
  },
  {
    id: 'realizar-avaliacao',
    title: '✍️ Realizando uma Avaliação',
    content: `Passo a passo para avaliar um funcionário de forma completa e justa.`,
    steps: [
      'Acesse o ciclo de avaliação ativo',
      'Selecione o funcionário a ser avaliado',
      'Revise o histórico e metas anteriores',
      'Avalie cada item dos 4 níveis (0 a 5)',
      'Adicione comentários e exemplos',
      'Preencha os campos de feedback',
      'Defina pontos fortes e áreas de melhoria',
      'Sugira ações de desenvolvimento',
      'Revise e salve a avaliação'
    ],
    tips: [
      'Reserve tempo adequado para cada avaliação',
      'Baseie-se em fatos e dados, não em impressões',
      'Dê feedback específico e acionável',
      'Inclua tanto pontos positivos quanto de melhoria'
    ]
  },
  {
    id: 'feedback',
    title: '💬 Dando Feedback Construtivo',
    content: `O feedback é a parte mais importante da avaliação. Deve ser 
    específico, construtivo e orientado ao desenvolvimento.`,
    steps: [
      'Comece pelos pontos positivos (reconhecimento)',
      'Apresente áreas de melhoria com exemplos',
      'Use a técnica SBI: Situação, Comportamento, Impacto',
      'Proponha ações concretas de desenvolvimento',
      'Estabeleça metas claras para o próximo período',
      'Ouça a perspectiva do funcionário',
      'Documente acordos e próximos passos'
    ],
    tips: [
      'Feedback deve ser uma conversa, não um monólogo',
      'Evite generalizações como "sempre" ou "nunca"',
      'Foque em comportamentos, não em personalidade',
      'Agende follow-ups para acompanhar progresso'
    ]
  },
  {
    id: 'relatorios-desempenho',
    title: '📈 Relatórios de Desempenho',
    content: `Acompanhe o desempenho da equipe com relatórios consolidados 
    e análises comparativas.`,
    steps: [
      'Acesse a aba "Relatórios" no dashboard de desempenho',
      'Visualize a distribuição de classificações',
      'Compare desempenho entre departamentos',
      'Identifique tendências ao longo dos ciclos',
      'Exporte relatórios para apresentações'
    ],
    tips: [
      'Use relatórios para identificar necessidades de treinamento',
      'Compare ciclos para ver evolução da equipe',
      'Compartilhe insights com a liderança'
    ]
  }
];

// ============================================
// COMPONENTE DE MANUAL
// ============================================
export default function UserManual({ 
  sections, 
  title = "Manual do Usuário",
  description = "Guia completo para utilizar a plataforma",
  triggerLabel = "Ajuda",
  triggerVariant = "outline"
}) {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant={triggerVariant} size="sm" className="gap-2" data-testid="help-manual-btn">
          <BookOpen className="w-4 h-4" />
          {triggerLabel}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-cyan-600" />
            {title}
          </SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>
        
        <ScrollArea className="h-[calc(100vh-120px)] mt-4 pr-4">
          <Accordion type="single" collapsible className="w-full">
            {sections.map((section, index) => (
              <AccordionItem key={section.id} value={section.id}>
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="font-medium">{section.title}</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pb-4">
                    {/* Content */}
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {section.content}
                    </p>
                    
                    {/* Steps */}
                    {section.steps && section.steps.length > 0 && (
                      <div className="space-y-2">
                        <p className="font-medium text-slate-800 text-sm flex items-center gap-2">
                          <ArrowRight className="w-4 h-4 text-cyan-600" />
                          {section.id.includes('nivel') || section.id.includes('pontuacao') || section.id.includes('classificacao') 
                            ? 'Detalhes:' 
                            : 'Passo a passo:'}
                        </p>
                        <ol className="space-y-2 ml-2">
                          {section.steps.map((step, stepIndex) => (
                            <li key={stepIndex} className="flex items-start gap-2 text-sm text-slate-600">
                              <span className="flex-shrink-0 w-5 h-5 bg-cyan-100 text-cyan-700 rounded-full flex items-center justify-center text-xs font-medium">
                                {stepIndex + 1}
                              </span>
                              <span>{step}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    
                    {/* Tips */}
                    {section.tips && section.tips.length > 0 && (
                      <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                        <p className="font-medium text-amber-800 text-sm flex items-center gap-2 mb-2">
                          <Lightbulb className="w-4 h-4" />
                          Dicas:
                        </p>
                        <ul className="space-y-1">
                          {section.tips.map((tip, tipIndex) => (
                            <li key={tipIndex} className="flex items-start gap-2 text-sm text-amber-700">
                              <Star className="w-3 h-3 mt-1 flex-shrink-0" />
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          
          {/* Footer */}
          <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-xs text-slate-500 flex items-center gap-2">
              <HelpCircle className="w-4 h-4" />
              Dúvidas? Entre em contato com nosso suporte.
            </p>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

// Exports específicos para cada manual
export function RecruiterManual() {
  return (
    <UserManual
      sections={recruiterManualSections}
      title="Manual do Recrutador"
      description="Guia completo para utilizar todas as funcionalidades de recrutamento"
      triggerLabel="Manual"
    />
  );
}

export function PerformanceManual() {
  return (
    <UserManual
      sections={performanceManualSections}
      title="Manual de Avaliação"
      description="Guia completo para realizar avaliações de desempenho"
      triggerLabel="Como Avaliar"
    />
  );
}
